import { useLocation, useNavigate } from "react-router-dom";
import useAuthentication from "../../../hooks/useAuthentication";
import { useForm } from "react-hook-form";
import {
  initialFormData,
  SettingsFormDataType,
  SettingsTableDataType,
} from "./types";
import usePopup from "../../../hooks/usePopup";
import { useMutation, useQuery } from "react-query";
import { getIcons, getMenuParentList, saveMasterData } from "./services";
import { useEffect } from "react";
import {
  Paper,
  Typography,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { CustomFormField } from "../../../utilis/component/CustomFormElement";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const pageTitle = "Menu ";
function AddOrEditMenu() {
  const { user, setRefetchPermission } = useAuthentication();
  const navigate = useNavigate();
  const { control, handleSubmit, reset } = useForm<SettingsFormDataType>({
    defaultValues: initialFormData,
  });
  const {
    state: { data = null },
  } = useLocation() as { state: { data: SettingsTableDataType } };
  const { showSnackbar } = usePopup();
  const handleGoBack = () => {
    navigate(-1);
  };

  const { data: parentMenus, isLoading: parentMenusLoading } = useQuery(
    ["getMenuParents"],
    getMenuParentList,
  );

  const { data: icons, isLoading: iconsLoading } = useQuery(
    ["getAllIcons"],
    getIcons,
  );

  const { mutate: save, isLoading } = useMutation(saveMasterData, {
    onSuccess: (result) => {
      if (result.responseIndicator == "success") {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "success",
          duration: 3000,
        });
        handleGoBack();
        setRefetchPermission(true);
      } else {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "warning",
          duration: 3000,
        });
      }
    },
    onError: (e: any) => {
      showSnackbar({
        message:
          e?.response?.data?.responseMessage ||
          e?.response?.data?.errorMessage ||
          "Something went wrong !",
        open: true,
        variant: "error",
        duration: 3000,
      });
    },
  });
  const onSubmit = (value: SettingsFormDataType) => {
    save({
      id: data?.id || null,
      name: value.name,
      code: value.code,
      status: value.status,
      icon: value.icon ? value.icon.icon : "",
      page: value.page,
      clinicId: Number(user?.clinicId),
      parentId: Number(value?.parent?.id),
      createdById: Number(user?.userId),
      createdByDate: "",
      ip: "",
      userMenuId: data?.usermenuId || null,
    });
  };

  const handleReset = () => {
    reset(initialFormData);
  };

  useEffect(() => {
    if (data) {
      reset({
        code: data.code,
        name: data.name,
        parent: data.parentId
          ? {
              id: data.parentId,
              name: data.parentName,
            }
          : null,
        icon: { icon: data.icon },
        page: data.page,
        status: data.status,
        userMenuId: data.usermenuId,
      });
    }
  }, [data]);

  return (
    <div className="p-3">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper>
          <div className="flex justify-between items-center p-2">
            <div>
              <Typography variant="h6" className="p-3">
                {data ? "Update" : "Add"} {pageTitle}
              </Typography>
            </div>
            <div className="flex gap-2">
              <Button
                disabled={isLoading}
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
              >
                back
              </Button>
              {!Boolean(data) && (
                <Button
                  disabled={isLoading}
                  color="error"
                  type="reset"
                  onClick={handleReset}
                  startIcon={<CloseIcon />}
                >
                  reset
                </Button>
              )}
              <Button
                disabled={isLoading}
                type="submit"
                color="success"
                startIcon={
                  isLoading ? (
                    <CircularProgress size={20} color="success" />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                {data ? "update" : "save"}
              </Button>
            </div>
          </div>
          <Divider />
          <div className="p-3 grid grid-cols-3 gap-3">
            <CustomFormField
              control={control}
              loading={parentMenusLoading}
              name="code"
              element="input"
              fieldProps={{ label: "Enter code " }}
              rules={{ required: "Code is required" }}
            />
            <CustomFormField
              control={control}
              name="name"
              element="input"
              fieldProps={{ label: "Enter name " }}
              rules={{ required: "Name is required" }}
            />
            <CustomFormField
              control={control}
              name="parent"
              element="autocomplete"
              options={parentMenus?.response || []}
              fieldProps={{ label: "Choose parent " }}
              autocompleteProps={{
                getOptionLabel: (option) => option.name,
                isOptionEqualToValue: (option, value) => option.id === value.id,
              }}
              rules={{ required: "Select parent" }}
            />
            <CustomFormField
              control={control}
              name="icon"
              element="autocomplete"
              loading={iconsLoading}
              options={icons || []}
              fieldProps={{ label: "Choose icon " }}
              autocompleteProps={{
                getOptionLabel: (option) => option.icon,
                isOptionEqualToValue: (option, value) =>
                  option.icon === value.icon,
              }}
              rules={{ required: false }}
            />
            <div className="col-span-2">
              <CustomFormField
                control={control}
                name="page"
                element="input"
                fieldProps={{ label: "Enter Path " }}
                rules={{ required: false }}
              />
            </div>
            <CustomFormField
              control={control}
              name="status"
              element="radio"
              radioProps={{ row: true }}
              radios={[
                { label: "Active", value: 1 },
                { label: "Inactive", value: 0 },
              ]}
              fieldProps={{ label: "Status " }}
              rules={{ required: "Status is required" }}
            />
          </div>
        </Paper>
      </form>
    </div>
  );
}

export default AddOrEditMenu;
